import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Drawer } from '../../components/Drawer.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { Button, Badge, Loading } from '../../components/ui.tsx';
import { MediaThumb } from '../../components/MediaThumb.tsx';
import { useMedia, useDeleteMedia, mediaFileUrl, putMediaThumb } from '../../api/media.ts';
import {
  useTemplates,
  usePatchMediaFields,
  defaultFields,
  type Template,
  type TemplateFields,
  type TemplateMedia,
} from '../../api/templates.ts';
import { TemplateFieldEditor, validateFields } from './TemplateFieldEditor.tsx';
import { TemplateLivePreview } from './TemplateLivePreview.tsx';
import { SandboxedHtmlFrame } from '../../components/SandboxedHtmlFrame.tsx';
import { usePlaylists, getPlaylist } from '../../api/playlists.ts';
import { usePermissions } from '../../auth/session.tsx';
import { ApiError } from '../../api/client.ts';
import { generateThumb } from '../../lib/thumbs.ts';
import { formatBytes, formatDuration, formatRelative } from '../../lib/format.ts';

/** Editable fields + live preview for an html_template media (docs/09 §B1). The
 *  flagship "изменяемые тексты" surface — edit a text, watch the slide re-render,
 *  save to push a new manifest version to screens. */
function TemplateEditSection({ media, canEdit }: { media: TemplateMedia; canEdit: boolean }) {
  const templates = useTemplates();
  const patch = usePatchMediaFields();
  const template: Template | undefined = templates.data?.find((t) => t.id === media.template_id);
  const [values, setValues] = useState<TemplateFields>(
    media.fields_json ?? (template ? defaultFields(template) : {}),
  );

  if (templates.isLoading) return <Loading />;
  if (!template) return <p className="text-dim">This template is no longer available.</p>;

  const errors = validateFields(template.fields, values);
  const dirty = JSON.stringify(values) !== JSON.stringify(media.fields_json ?? {});
  const canSave = canEdit && dirty && Object.keys(errors).length === 0 && !patch.isPending;

  function save() {
    patch.mutate(
      { id: media.id, fields: values },
      {
        onSuccess: () => toast.success('Slide updated'),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update the slide'),
      },
    );
  }

  return (
    <div className="tpl-edit">
      <div className="tpl-preview-label">Live preview</div>
      <TemplateLivePreview templateId={template.id} fields={values} />
      {canEdit && (
        <>
          <div className="divider" />
          <TemplateFieldEditor fields={template.fields} values={values} onChange={setValues} />
          <div className="row mt-2">
            <Button variant="primary" size="sm" onClick={save} disabled={!canSave}>
              {patch.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            {dirty && (
              <Button size="sm" variant="ghost" onClick={() => setValues(media.fields_json ?? {})}>
                Reset
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Sandboxed live render + a "view source" toggle for an html_snippet (docs/09 §B3). */
function SnippetSection({ media }: { media: TemplateMedia }) {
  const [showSource, setShowSource] = useState(false);
  const source = useQuery({
    queryKey: ['media-source', media.id],
    enabled: showSource,
    queryFn: () => fetch(mediaFileUrl(media.id), { credentials: 'include' }).then((r) => r.text()),
  });
  return (
    <div className="tpl-edit">
      <div className="tpl-preview">
        <div className="tpl-preview-stage">
          <SandboxedHtmlFrame
            mediaId={media.id}
            title="Snippet preview"
            className="tpl-preview-frame"
          />
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => setShowSource((s) => !s)}>
        {showSource ? 'Hide source' : 'View source'}
      </Button>
      {showSource && (
        <pre className="tpl-source">{source.isLoading ? 'Loading…' : source.data ?? ''}</pre>
      )}
    </div>
  );
}

export function MediaDetailDrawer({ mediaId, onClose }: { mediaId: string; onClose: () => void }) {
  const media = useMedia();
  const perms = usePermissions();
  const del = useDeleteMedia();
  const playlists = usePlaylists();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);
  const [genBusy, setGenBusy] = useState(false);

  const item = media.data?.find((m) => m.id === mediaId);

  // GAP: no "references" endpoint — compute usage by scanning playlist items.
  const usage = useQuery({
    queryKey: ['media-refs', mediaId, (playlists.data ?? []).map((p) => p.id)],
    enabled: !!playlists.data,
    queryFn: async () => {
      const details = await Promise.all((playlists.data ?? []).map((p) => getPlaylist(p.id)));
      return details.filter((d) => d.items.some((it) => it.media_id === mediaId)).map((d) => d.name);
    },
  });

  if (media.isLoading) {
    return (
      <Drawer title="Media" onClose={onClose}>
        <Loading />
      </Drawer>
    );
  }
  if (!item) {
    return (
      <Drawer title="Media" onClose={onClose}>
        <p className="text-dim">This item no longer exists.</p>
      </Drawer>
    );
  }

  const usedIn = usage.data ?? [];

  // Backfill for media uploaded before thumbs existed (or whose generation failed).
  // No has_thumb !== undefined check: the field may be absent even on media that
  // genuinely has no thumb, so the button is offered whenever it isn't known-true.
  const canGeneratePreview =
    perms.canManageContent && (item.kind === 'image' || item.kind === 'video') && item.has_thumb !== true;

  async function generatePreview() {
    if (!item) return;
    setGenBusy(true);
    try {
      const res = await fetch(mediaFileUrl(item.id), { credentials: 'include' });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const file = new File([blob], item.filename ?? 'media', { type: item.mime ?? blob.type });
      const thumb = await generateThumb(file, item.kind as 'image' | 'video');
      if (!thumb) {
        toast.error("Couldn't generate a preview for this file.");
        return;
      }
      await putMediaThumb(item.id, thumb);
      toast.success('Preview generated');
      void qc.invalidateQueries({ queryKey: ['media'] });
    } catch {
      toast.error("Couldn't generate a preview for this file.");
    } finally {
      setGenBusy(false);
    }
  }

  return (
    <Drawer title={item.filename ?? 'Media'} onClose={onClose}>
      {item.kind === 'html_template' ? (
        <TemplateEditSection media={item} canEdit={perms.canManageContent} />
      ) : item.kind === 'html_snippet' ? (
        <SnippetSection media={item} />
      ) : (
        <div className="preview-box mb-4">
          <MediaThumb media={item} className="media-thumb" />
        </div>
      )}

      <dl className="dl">
        <dt>Kind</dt>
        <dd>
          <Badge tone="neutral">{item.kind}</Badge>{' '}
          {item.status !== 'ready' && <Badge tone="warn">{item.status}</Badge>}
        </dd>
        {item.kind === 'url' ? (
          <>
            <dt>Source</dt>
            <dd className="mono" style={{ wordBreak: 'break-all' }}>
              {item.src_url}
            </dd>
          </>
        ) : (
          <>
            <dt>Dimensions</dt>
            <dd>{item.width && item.height ? `${item.width}×${item.height}` : '—'}</dd>
            <dt>Size</dt>
            <dd>{formatBytes(item.size_bytes)}</dd>
            {item.kind === 'video' && (
              <>
                <dt>Duration</dt>
                <dd>{formatDuration(item.duration_ms)}</dd>
              </>
            )}
          </>
        )}
        <dt>Added</dt>
        <dd>{formatRelative(item.created_at)}</dd>
        <dt>Used in</dt>
        <dd>
          {usage.isLoading ? (
            'checking…'
          ) : usedIn.length === 0 ? (
            'Not used in any playlist'
          ) : (
            <>
              {usedIn.length} playlist{usedIn.length === 1 ? '' : 's'}: {usedIn.join(', ')}
            </>
          )}
        </dd>
      </dl>

      {blockedMsg && (
        <div className="hint-box warn mt-4" role="alert">
          Can&apos;t delete: {blockedMsg}
        </div>
      )}

      {canGeneratePreview && (
        <>
          <div className="divider" />
          <Button size="sm" onClick={generatePreview} disabled={genBusy}>
            {genBusy ? 'Generating…' : 'Generate preview'}
          </Button>
        </>
      )}

      {perms.canManageContent && (
        <>
          <div className="divider" />
          <Button variant="danger" onClick={() => setConfirm(true)}>
            Delete media
          </Button>
        </>
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete media?"
          message={`“${item.filename}” will be removed. This can't be undone.`}
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirm(false)}
          onConfirm={() =>
            del.mutate(item.id, {
              onSuccess: () => {
                toast.success('Media deleted');
                onClose();
              },
              onError: (e) => {
                setConfirm(false);
                if (e instanceof ApiError && e.isConflict) setBlockedMsg(e.message);
                else toast.error(e instanceof ApiError ? e.message : 'Failed');
              },
            })
          }
        />
      )}
    </Drawer>
  );
}
